import {
  ChevronDown,
  ChevronRight,
  Copy,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useOrganization } from '../../contexts';
import {
  apiKeyScopeService,
  organizationService,
  toolService,
  ApiKeyScopeForTool,
} from '../../services';
import { ApiKey, ApiKeyResponse } from '../../types';
import { toolToast } from '../../utils/toolToast';
import { Button, Card, ConfirmModal, Input, Modal } from '../ui';

type GeneratedApiKey = ApiKeyResponse | null;

type ApiKeyState = {
  expanded: boolean;
  scopes: ApiKeyScopeForTool[];
  isLoadingScopes: boolean;
};

const copyToClipboard = (value: string, successMessage: string) => {
  if (typeof navigator === 'undefined' || !navigator.clipboard) {
    toolToast.error('Clipboard nao disponivel');
    return;
  }
  navigator.clipboard
    .writeText(value)
    .then(() => toolToast.success(successMessage))
    .catch(() => toolToast.error('Falha ao copiar'));
};

export const ApiKeysPermissionsCard: React.FC = () => {
  const { t } = useTranslation();
  const { currentOrganization } = useOrganization();

  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [keyStates, setKeyStates] = useState<Record<number, ApiKeyState>>({});
  const [tools, setTools] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createKeyName, setCreateKeyName] = useState('');
  const [createKeyDescription, setCreateKeyDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<GeneratedApiKey>(null);

  const [deleteKeyTarget, setDeleteKeyTarget] = useState<ApiKey | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [addToolModalKey, setAddToolModalKey] = useState<ApiKey | null>(null);
  const [addToolSelectedId, setAddToolSelectedId] = useState<string>('');
  const [addToolCanWrite, setAddToolCanWrite] = useState(true);
  const [isAddingTool, setIsAddingTool] = useState(false);

  const loadApiKeys = useCallback(async () => {
    if (!currentOrganization?.id) return;
    setIsLoadingKeys(true);
    try {
      const result = await organizationService.listApiKeys(
        currentOrganization.id,
        false
      );
      setApiKeys(result);
    } catch (error) {
      toolToast.error(
        error instanceof Error
          ? error.message
          : t('profile.apiKeys.loadError')
      );
    } finally {
      setIsLoadingKeys(false);
    }
  }, [currentOrganization?.id, t]);

  const loadTools = useCallback(async () => {
    try {
      const result = await toolService.list({ page: 1, page_size: 200 });
      setTools(result.items.map(item => ({ id: item.id, name: item.name })));
    } catch (error) {
      // Mantemos o card funcional mesmo sem lista de tools (o owner ainda
      // consegue ver suas keys e scopes existentes).
    }
  }, []);

  useEffect(() => {
    void loadApiKeys();
    void loadTools();
  }, [loadApiKeys, loadTools]);

  const loadScopesForKey = useCallback(
    async (apiKeyId: number) => {
      if (!currentOrganization?.id) return;
      setKeyStates(prev => ({
        ...prev,
        [apiKeyId]: {
          expanded: prev[apiKeyId]?.expanded ?? false,
          scopes: prev[apiKeyId]?.scopes ?? [],
          isLoadingScopes: true,
        },
      }));
      try {
        const result = await apiKeyScopeService.listScopesByApiKey(
          currentOrganization.id,
          apiKeyId
        );
        setKeyStates(prev => ({
          ...prev,
          [apiKeyId]: {
            expanded: prev[apiKeyId]?.expanded ?? true,
            scopes: result.items,
            isLoadingScopes: false,
          },
        }));
      } catch (error) {
        setKeyStates(prev => ({
          ...prev,
          [apiKeyId]: {
            expanded: prev[apiKeyId]?.expanded ?? false,
            scopes: prev[apiKeyId]?.scopes ?? [],
            isLoadingScopes: false,
          },
        }));
        toolToast.error(
          error instanceof Error
            ? error.message
            : t('profile.apiKeys.scopeLoadError')
        );
      }
    },
    [currentOrganization?.id, t]
  );

  const handleToggleKey = useCallback(
    (apiKeyId: number) => {
      setKeyStates(prev => {
        const current = prev[apiKeyId] ?? {
          expanded: false,
          scopes: [],
          isLoadingScopes: false,
        };
        const willExpand = !current.expanded;
        if (willExpand && current.scopes.length === 0 && !current.isLoadingScopes) {
          void loadScopesForKey(apiKeyId);
        }
        return {
          ...prev,
          [apiKeyId]: { ...current, expanded: willExpand },
        };
      });
    },
    [loadScopesForKey]
  );

  const handleTogglePermission = useCallback(
    async (
      apiKeyId: number,
      toolId: string,
      field: 'can_read' | 'can_write',
      checked: boolean
    ) => {
      if (!currentOrganization?.id) return;
      const current = keyStates[apiKeyId]?.scopes.find(s => s.tool_id === toolId);
      if (!current) return;
      const nextScope = {
        can_read: field === 'can_read' ? checked : current.can_read,
        can_write: field === 'can_write' ? checked : current.can_write,
      };
      try {
        const updated = await apiKeyScopeService.upsertScopeForTool(
          currentOrganization.id,
          apiKeyId,
          toolId,
          nextScope
        );
        setKeyStates(prev => {
          const state = prev[apiKeyId];
          if (!state) return prev;
          return {
            ...prev,
            [apiKeyId]: {
              ...state,
              scopes: state.scopes.map(s =>
                s.tool_id === toolId
                  ? { ...s, can_read: updated.can_read, can_write: updated.can_write }
                  : s
              ),
            },
          };
        });
        toolToast.success(t('profile.apiKeys.permissionSaved'));
      } catch (error) {
        toolToast.error(
          error instanceof Error
            ? error.message
            : t('profile.apiKeys.permissionSaveError')
        );
      }
    },
    [currentOrganization?.id, keyStates, t]
  );

  const handleRemoveScope = useCallback(
    async (apiKeyId: number, toolId: string) => {
      if (!currentOrganization?.id) return;
      try {
        await apiKeyScopeService.deleteScopeForTool(
          currentOrganization.id,
          apiKeyId,
          toolId
        );
        setKeyStates(prev => {
          const state = prev[apiKeyId];
          if (!state) return prev;
          return {
            ...prev,
            [apiKeyId]: {
              ...state,
              scopes: state.scopes.filter(s => s.tool_id !== toolId),
            },
          };
        });
        toolToast.success(t('profile.apiKeys.scopeRemoved'));
      } catch (error) {
        toolToast.error(
          error instanceof Error
            ? error.message
            : t('profile.apiKeys.scopeRemoveError')
        );
      }
    },
    [currentOrganization?.id, t]
  );

  const handleOpenAddTool = useCallback((apiKey: ApiKey) => {
    setAddToolModalKey(apiKey);
    setAddToolSelectedId('');
    setAddToolCanWrite(true);
  }, []);

  const handleConfirmAddTool = useCallback(async () => {
    if (!currentOrganization?.id || !addToolModalKey || !addToolSelectedId) return;
    setIsAddingTool(true);
    try {
      const created = await apiKeyScopeService.upsertScopeForTool(
        currentOrganization.id,
        addToolModalKey.id,
        addToolSelectedId,
        { can_read: true, can_write: addToolCanWrite }
      );
      setKeyStates(prev => {
        const state = prev[addToolModalKey.id] ?? {
          expanded: true,
          scopes: [],
          isLoadingScopes: false,
        };
        const exists = state.scopes.some(s => s.tool_id === created.tool_id);
        return {
          ...prev,
          [addToolModalKey.id]: {
            ...state,
            expanded: true,
            scopes: exists
              ? state.scopes.map(s => (s.tool_id === created.tool_id ? created : s))
              : [...state.scopes, created],
          },
        };
      });
      setAddToolModalKey(null);
      toolToast.success(t('profile.apiKeys.scopeAdded'));
    } catch (error) {
      toolToast.error(
        error instanceof Error
          ? error.message
          : t('profile.apiKeys.scopeAddError')
      );
    } finally {
      setIsAddingTool(false);
    }
  }, [
    currentOrganization?.id,
    addToolModalKey,
    addToolSelectedId,
    addToolCanWrite,
    t,
  ]);

  const handleCreateApiKey = useCallback(async () => {
    if (!currentOrganization?.id || !createKeyName.trim()) return;
    setIsCreating(true);
    try {
      const result = await organizationService.generateApiKey(
        currentOrganization.id,
        { name: createKeyName.trim(), description: createKeyDescription.trim() }
      );
      setGeneratedKey(result);
      setIsCreateModalOpen(false);
      setCreateKeyName('');
      setCreateKeyDescription('');
      await loadApiKeys();
    } catch (error) {
      toolToast.error(
        error instanceof Error
          ? error.message
          : t('profile.apiKeys.createError')
      );
    } finally {
      setIsCreating(false);
    }
  }, [currentOrganization?.id, createKeyName, createKeyDescription, loadApiKeys, t]);

  const handleConfirmDeleteKey = useCallback(async () => {
    if (!currentOrganization?.id || !deleteKeyTarget) return;
    setIsDeleting(true);
    try {
      await organizationService.deleteApiKey(
        currentOrganization.id,
        deleteKeyTarget.id
      );
      setDeleteKeyTarget(null);
      await loadApiKeys();
      toolToast.success(t('profile.apiKeys.deleted'));
    } catch (error) {
      toolToast.error(
        error instanceof Error
          ? error.message
          : t('profile.apiKeys.deleteError')
      );
    } finally {
      setIsDeleting(false);
    }
  }, [currentOrganization?.id, deleteKeyTarget, loadApiKeys, t]);

  const availableToolsForKey = useMemo(() => {
    if (!addToolModalKey) return tools;
    const usedToolIds = new Set(
      keyStates[addToolModalKey.id]?.scopes.map(s => s.tool_id) ?? []
    );
    return tools.filter(tool => !usedToolIds.has(tool.id));
  }, [addToolModalKey, keyStates, tools]);

  return (
    <Card>
      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              {t('profile.apiKeys.title')}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('profile.apiKeys.description')}
            </p>
          </div>
          <Button
            size="sm"
            variant="primary"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <Plus className="mr-1 h-4 w-4" />
            {t('profile.apiKeys.createButton')}
          </Button>
        </div>

        {isLoadingKeys ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
          </div>
        ) : apiKeys.length === 0 ? (
          <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
            {t('profile.apiKeys.empty')}
          </p>
        ) : (
          <ul className="space-y-2">
            {apiKeys.map(apiKey => {
              const state = keyStates[apiKey.id];
              const isOpen = state?.expanded ?? false;
              return (
                <li
                  key={apiKey.id}
                  className="rounded border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center gap-2 px-3 py-2">
                    <button
                      type="button"
                      onClick={() => handleToggleKey(apiKey.id)}
                      className="flex flex-1 items-center gap-2 text-left"
                    >
                      {isOpen ? (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-500" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {apiKey.name}
                          </span>
                          {!apiKey.is_active && (
                            <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                              {t('profile.apiKeys.inactive')}
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                          <code>{apiKey.api_key_masked}</code>
                          {apiKey.last_used_at && (
                            <span>
                              · {t('profile.apiKeys.lastUsed')}{' '}
                              {new Date(apiKey.last_used_at).toLocaleDateString('pt-BR')}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteKeyTarget(apiKey)}
                      className="rounded p-1 text-gray-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-900/30"
                      aria-label={t('profile.apiKeys.deleteAria')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {isOpen && (
                    <div className="border-t border-gray-200 p-3 dark:border-gray-700">
                      <div className="mb-2 flex items-center justify-between">
                        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                          {t('profile.apiKeys.toolsAccess')}
                        </h4>
                        <button
                          type="button"
                          onClick={() => handleOpenAddTool(apiKey)}
                          className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                        >
                          <Plus className="h-3 w-3" />
                          {t('profile.apiKeys.addToolButton')}
                        </button>
                      </div>

                      {state?.isLoadingScopes ? (
                        <div className="flex items-center gap-2 py-2 text-xs text-gray-500">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          {t('profile.apiKeys.loadingScopes')}
                        </div>
                      ) : !state?.scopes?.length ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t('profile.apiKeys.noScopes')}
                        </p>
                      ) : (
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-gray-200 text-left text-gray-500 dark:border-gray-700 dark:text-gray-400">
                              <th className="py-1 pr-2 font-medium">
                                {t('profile.apiKeys.toolHeader')}
                              </th>
                              <th className="py-1 pr-2 text-center font-medium">
                                {t('profile.apiKeys.readHeader')}
                              </th>
                              <th className="py-1 pr-2 text-center font-medium">
                                {t('profile.apiKeys.writeHeader')}
                              </th>
                              <th className="py-1 font-medium" />
                            </tr>
                          </thead>
                          <tbody className="text-gray-800 dark:text-gray-100">
                            {state.scopes.map(scope => (
                              <tr
                                key={scope.tool_id}
                                className="border-b border-gray-100 last:border-b-0 dark:border-gray-700/60"
                              >
                                <td className="py-1 pr-2">{scope.tool_name}</td>
                                <td className="py-1 pr-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={scope.can_read}
                                    onChange={e =>
                                      void handleTogglePermission(
                                        apiKey.id,
                                        scope.tool_id,
                                        'can_read',
                                        e.target.checked
                                      )
                                    }
                                  />
                                </td>
                                <td className="py-1 pr-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={scope.can_write}
                                    onChange={e =>
                                      void handleTogglePermission(
                                        apiKey.id,
                                        scope.tool_id,
                                        'can_write',
                                        e.target.checked
                                      )
                                    }
                                  />
                                </td>
                                <td className="py-1 text-right">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      void handleRemoveScope(apiKey.id, scope.tool_id)
                                    }
                                    className="rounded p-1 text-gray-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-900/30"
                                    aria-label={t('profile.apiKeys.removeScope')}
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title={t('profile.apiKeys.createModalTitle')}
      >
        <div className="space-y-3 p-4">
          <Input
            label={t('profile.apiKeys.nameLabel')}
            value={createKeyName}
            onChange={e => setCreateKeyName(e.target.value)}
            placeholder="my-prod-key"
          />
          <Input
            label={t('profile.apiKeys.descriptionLabel')}
            value={createKeyDescription}
            onChange={e => setCreateKeyDescription(e.target.value)}
            placeholder={t('profile.apiKeys.descriptionPlaceholder')}
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsCreateModalOpen(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              onClick={() => void handleCreateApiKey()}
              loading={isCreating}
              disabled={!createKeyName.trim()}
            >
              {t('profile.apiKeys.create')}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(generatedKey)}
        onClose={() => setGeneratedKey(null)}
        title={t('profile.apiKeys.generatedTitle')}
      >
        <div className="space-y-3 p-4">
          <p className="text-xs text-amber-700 dark:text-amber-300">
            {t('profile.apiKeys.generatedWarning')}
          </p>
          <div className="space-y-2">
            <div className="text-xs font-medium text-gray-500">
              {t('profile.apiKeys.apiKeyLabel')}
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded bg-gray-100 p-2 text-xs dark:bg-gray-900">
                {generatedKey?.api_key}
              </code>
              <button
                type="button"
                onClick={() =>
                  copyToClipboard(
                    generatedKey?.api_key || '',
                    t('profile.apiKeys.copied')
                  )
                }
                className="rounded border border-gray-200 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700"
              >
                <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button size="sm" onClick={() => setGeneratedKey(null)}>
              {t('common.close')}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(addToolModalKey)}
        onClose={() => setAddToolModalKey(null)}
        title={t('profile.apiKeys.addToolTitle')}
      >
        <div className="space-y-3 p-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('profile.apiKeys.addToolDescription', { name: addToolModalKey?.name })}
          </p>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
              {t('profile.apiKeys.selectToolLabel')}
            </label>
            <select
              value={addToolSelectedId}
              onChange={e => setAddToolSelectedId(e.target.value)}
              className="w-full rounded border border-gray-300 bg-white p-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
            >
              <option value="">{t('profile.apiKeys.selectToolPlaceholder')}</option>
              {availableToolsForKey.map(tool => (
                <option key={tool.id} value={tool.id}>
                  {tool.name}
                </option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={addToolCanWrite}
              onChange={e => setAddToolCanWrite(e.target.checked)}
            />
            {t('profile.apiKeys.grantWrite')}
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAddToolModalKey(null)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              onClick={() => void handleConfirmAddTool()}
              loading={isAddingTool}
              disabled={!addToolSelectedId}
            >
              {t('profile.apiKeys.add')}
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={Boolean(deleteKeyTarget)}
        onClose={() => setDeleteKeyTarget(null)}
        onConfirm={() => void handleConfirmDeleteKey()}
        title={t('profile.apiKeys.deleteConfirmTitle')}
        message={t('profile.apiKeys.deleteConfirmDescription', {
          name: deleteKeyTarget?.name,
        })}
        confirmText={t('profile.apiKeys.deleteConfirmCta')}
        cancelText={t('common.cancel')}
        isLoading={isDeleting}
        variant="danger"
      />
    </Card>
  );
};

export default ApiKeysPermissionsCard;
